import { Stack } from '@mui/material';
import { useMemo } from 'react';
import { Control, useWatch } from 'react-hook-form';

import { FormFieldsDisabledProvider } from '@/components/form/context/formFieldsDisabled.provider';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { getOptionsFromData as getTrusteeOptionsFromData } from '@/modules/entities/inputs/TrusteeDropdown/TrusteeDropdown';
import { useOptionsForTrustDetailsForm } from '@/modules/entities/TrustDetailsSubform/graphql/hooks';
import { useTrustDetailsFormOptionsQuery } from '@/modules/entities/TrustDetailsSubform/graphql/TrustDetails.generated';
import { useNotes } from '@/modules/entities/TrustDetailsSubform/hooks/useNotes';
import { SuccessorTrusteeInputRepeater } from '@/modules/entities/TrustDetailsSubform/SuccessorTrusteeInputRepeater';
import { SuccessorTrusteeNotes } from '@/modules/entities/TrustDetailsSubform/SuccessorTrusteeNotes';
import { TrustAdvisorInputRepeater } from '@/modules/entities/TrustDetailsSubform/TrustAdvisorInputRepeater';
import { TrustAdvisorNotes } from '@/modules/entities/TrustDetailsSubform/TrustAdvisorNotes';
import {
  Fields,
  getTrustDetailsDefaultValues,
  TRUST_DETAILS_SUBFORM_NAMESPACE,
} from '@/modules/entities/TrustDetailsSubform/TrustDetailsSubform.types';
import { hasTrustAdvisors } from '@/modules/entities/TrustDetailsSubform/TrustDetailsSubform.utils';
import { TrusteeInputRepeater } from '@/modules/entities/TrustDetailsSubform/TrusteeInputRepeater';
import { TrusteeNotes } from '@/modules/entities/TrustDetailsSubform/TrusteeNotes';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AiSuggestionKind } from '@/types/schema';

import { ControllingPartyInput } from '../components';
import {
  controllingPartyEntityKindOptions,
  TESTAMENTARY_ENTITY,
} from '../EditTestamentaryEntitySplitScreen.constants';
import {
  EditTestamentaryEntityFormField,
  EditTestamentaryEntityFormShape,
} from '../EditTestamentaryEntitySplitScreen.types';

export function TrusteesSection() {
  const { reportError } = useReportError();
  const { householdId } = useHouseholdDetailsContext();

  const { control } = useFormContext<EditTestamentaryEntityFormShape>();

  const subformValues = useWatch({
    control,
    name: TRUST_DETAILS_SUBFORM_NAMESPACE,
    defaultValue: getTrustDetailsDefaultValues(
      'TrusteesSuccessorTrusteesAndTrustAdvisors'
    ),
  });

  const entityKind = useWatch({
    control,
    name: `${TESTAMENTARY_ENTITY}.testamentaryEntityKind` as const satisfies EditTestamentaryEntityFormField,
  });

  const isControllingPartyEntityKind = useMemo(() => {
    if (entityKind === '') {
      return false;
    }

    return controllingPartyEntityKindOptions.includes(entityKind);
  }, [entityKind]);

  // Show trust advisors only for non-controlling-party entity kinds
  const showTrustAdvisors = !isControllingPartyEntityKind;

  // Fetch trustee options
  const { data, loading, error } = useTrustDetailsFormOptionsQuery({
    // This section mounts/unmounts as users navigate between modal sections.
    // cache-first prevents avoidable network requests (and UI lag) when revisiting.
    fetchPolicy: 'cache-first',
    variables: {
      where: {
        id: householdId,
      },
    },
    skip: !householdId,
  });

  const possibleTrusteeOptions = getTrusteeOptionsFromData({
    data: data?.households.edges?.[0]?.node ?? null,
    subformValues,
  });

  const { trusteeOptions, trustAdvisorOptions, successorTrusteeOptions } =
    useOptionsForTrustDetailsForm({ subformValues, possibleTrusteeOptions });

  // Notes visibility
  const {
    shouldShowTrusteeNotes,
    shouldShowSuccessorTrusteeNotes,
    shouldShowTrustAdvisorNotes,
  } = useNotes({
    subformValues: subformValues || null,
    isTrusteesAndSuccessTrusteesOnly: isControllingPartyEntityKind,
  });

  // Cast control to the expected type for TrustDetailsSubform components
  const trustDetailsControl = control as unknown as Control<Fields>;

  // Enable duplicate grouping when AI suggestions are enabled
  const subtrustAISuggestionsEnabled = useFeatureFlag(
    'aikg_v2_associations_poc'
  );
  const shouldGroupDuplicates = subtrustAISuggestionsEnabled;

  // Trust advisor notes component
  const trustAdvisorNotes = (() => {
    if (hasTrustAdvisors(subformValues)) {
      return (
        <TrustAdvisorNotes
          control={trustDetailsControl}
          subformValues={subformValues}
          trusteeOptions={trusteeOptions}
        />
      );
    }
    return null;
  })();

  if (error) {
    reportError('failed to fetch trust details form options', error);
  }

  if (!householdId) {
    return null;
  }

  return (
    <FormFieldsDisabledProvider isDisabled={loading}>
      {/* Controlling Party Input (for controlling party entity kinds) */}
      {isControllingPartyEntityKind && (
        <FormLayoutRow>
          <FormLayoutItem>
            <ControllingPartyInput
              subformValues={subformValues}
              filterClientProfileIds={[]}
              householdId={householdId}
            />
          </FormLayoutItem>
        </FormLayoutRow>
      )}

      {/* Trustees Section */}
      <Stack spacing={2}>
        <FormLayoutRow>
          <FormLayoutItem width={12}>
            <TrusteeInputRepeater
              control={trustDetailsControl}
              householdId={householdId}
              trusteesWithoutDuties={false}
              trustPersonOptions={trusteeOptions}
              shouldGroupDuplicates={shouldGroupDuplicates}
              splitScreen
              suggestionKindOverride={AiSuggestionKind.SubtrustTrustee}
            />
          </FormLayoutItem>
        </FormLayoutRow>
        {shouldShowTrusteeNotes && subformValues && (
          <TrusteeNotes
            control={trustDetailsControl}
            subformValues={subformValues}
            trusteeOptions={trusteeOptions}
          />
        )}

        {/* Successor Trustees Section */}
        <FormLayoutRow>
          <FormLayoutItem width={12}>
            <SuccessorTrusteeInputRepeater
              control={trustDetailsControl}
              householdId={householdId}
              trusteesWithoutDuties={false}
              trustPersonOptions={successorTrusteeOptions}
              shouldGroupDuplicates={shouldGroupDuplicates}
              splitScreen
              suggestionKindOverride={AiSuggestionKind.SubtrustSuccessorTrustee}
            />
          </FormLayoutItem>
        </FormLayoutRow>
        {shouldShowSuccessorTrusteeNotes && subformValues && (
          <SuccessorTrusteeNotes
            control={trustDetailsControl}
            subformValues={subformValues}
            trusteeOptions={trusteeOptions}
          />
        )}

        {/* Trust Advisors Section (only for non-controlling-party kinds) */}
        {showTrustAdvisors && (
          <FormLayoutRow>
            <FormLayoutItem width={12}>
              <TrustAdvisorInputRepeater
                control={trustDetailsControl}
                householdId={householdId}
                trusteesWithoutDuties={false}
                trustPersonOptions={trustAdvisorOptions}
                shouldGroupDuplicates={shouldGroupDuplicates}
                splitScreen
                suggestionKindOverride={AiSuggestionKind.SubtrustTrustAdvisor}
              />
            </FormLayoutItem>
          </FormLayoutRow>
        )}
        {shouldShowTrustAdvisorNotes && trustAdvisorNotes}
      </Stack>
    </FormFieldsDisabledProvider>
  );
}
